/**
 * Subscribe to a company's presence room over the WebSocket and keep the
 * Redux `presenceReducer` in sync via push events.
 *
 * Behaviour:
 *  - On mount + company change: socket.get('/{companyId}/sockets/subscribe')
 *    → server joins us to `company:{X}` and acks with the initial snapshot.
 *  - Listens for `avatar-changed` events from the server.
 *  - On reconnect: re-subscribes + triggers a full `fetchOnlineThunk` for safety.
 *
 * Polling (`fetchOnlineThunk`) stays alive in the panel as a fallback in case the
 * socket is unavailable or events get dropped.
 */
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { socket, socketGet } from "services/socket-client";
import {
  setPresenceSnapshot,
  avatarChanged,
  applyUserOnline,
  applyUserOffline,
  callPresenceChanged,
  fetchOnlineThunk,
} from "./presence.slice";
import type { OnlineUser } from "services/presence.service";

interface SubscribeResponse {
  subscribed: boolean;
  snapshot: {
    self: OnlineUser | null;
    online: OnlineUser[];
    offline: OnlineUser[];
  };
}

interface AvatarChangedPayload {
  userId: string;
  avatarFileId: string | null;
}

interface UserOnlinePayload {
  companyId: string;
  user: OnlineUser;
}

interface UserOfflinePayload {
  companyId: string;
  userId: string;
}

interface CallPresencePayload {
  userId: string;
  inCall: boolean;
}

export function useCompanyPresenceSocket(companyId: string | undefined) {
  const dispatch: AppDispatch = useDispatch();
  const lastCompanyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!companyId) return;
    lastCompanyRef.current = companyId;
    let cancelled = false;

    const subscribe = async () => {
      try {
        const data = await socketGet<SubscribeResponse>(
          `/${companyId}/sockets/subscribe`
        );
        if (cancelled || lastCompanyRef.current !== companyId) return;
        if (data?.snapshot) {
          dispatch(setPresenceSnapshot(data.snapshot));
        }
      } catch (err) {
        // Keep a single warn — diagnostic without spamming production. Polling
        // fallback in the panel recovers presence state regardless.
        const status = (err as { statusCode?: number })?.statusCode;
        // eslint-disable-next-line no-console
        console.warn("[presence-socket] subscribe failed", { companyId, statusCode: status });
      }
    };

    const onAvatarChanged = (payload: AvatarChangedPayload) => {
      if (!payload || typeof payload.userId !== "string") return;
      dispatch(avatarChanged(payload));
    };

    const onUserOnline = (...args: unknown[]) => {
      const payload = args[0] as UserOnlinePayload | undefined;
      if (!payload?.user || String(payload.companyId) !== String(companyId)) return;
      dispatch(applyUserOnline({ user: payload.user }));
    };

    const onUserOffline = (...args: unknown[]) => {
      const payload = args[0] as UserOfflinePayload | undefined;
      if (!payload?.userId || String(payload.companyId) !== String(companyId)) return;
      dispatch(applyUserOffline({ userId: payload.userId }));
    };

    const onCallPresence = (...args: unknown[]) => {
      const payload = args[0] as CallPresencePayload | undefined;
      if (!payload?.userId) return;
      dispatch(
        callPresenceChanged({ userId: payload.userId, inCall: !!payload.inCall })
      );
    };

    const onReconnect = () => {
      subscribe();
      // Full sync — events dropped while disconnected won't be missed
      if (lastCompanyRef.current) {
        dispatch(fetchOnlineThunk(lastCompanyRef.current));
      }
    };

    socket.on("avatar-changed", onAvatarChanged as any);
    socket.on("user-online", onUserOnline as any);
    socket.on("user-offline", onUserOffline as any);
    socket.on("call-presence", onCallPresence as any);
    socket.on("reconnect", onReconnect as any);
    subscribe();

    return () => {
      cancelled = true;
      socket.off("avatar-changed", onAvatarChanged as any);
      socket.off("user-online", onUserOnline as any);
      socket.off("user-offline", onUserOffline as any);
      socket.off("call-presence", onCallPresence as any);
      socket.off("reconnect", onReconnect as any);
    };
  }, [companyId, dispatch]);
}
